import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchLeaveLimits, 
  addLeaveLimit, 
  updateLeaveLimit, 
  deleteLeaveLimit,
  toggleLeaveLimitStatus,
  toggleLeaveLimitAutoAssign
} from '../../redux/leaveLimitSlice';
import { 
  fetchLeaveTypes, 
  addLeaveType, 
  updateLeaveType, 
  deleteLeaveType 
} from '../../redux/leaveSlice';
import { 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Settings,
  Calendar,
  Users,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const LeaveTypeManagement = () => {
  const dispatch = useDispatch();
  const { leaveLimits, status } = useSelector((state) => state.leaveLimits);
  const { leaveTypes } = useSelector((state) => state.leaves);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    leaveType: '',
    maxDays: 0,
    isPaid: true,
    description: '',
    isActive: true,
    autoAssign: true
  });

  useEffect(() => {
    dispatch(fetchLeaveLimits());
  }, [dispatch]);

  const handleInitializeDefaultTypes = async () => {
    try {
      const response = await fetch('/api/hr/initialize-leave-types', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Default leave types initialized successfully!');
        dispatch(fetchLeaveLimits()); // Refresh the list
      } else {
        toast.error('Failed to initialize leave types');
      }
    } catch (error) {
      toast.error('Error initializing leave types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await dispatch(updateLeaveLimit({ 
          id: currentItem.id, 
          leaveLimitData: formData 
        })).unwrap();
        toast.success('Leave limit updated successfully!');
      } else {
        await dispatch(addLeaveLimit(formData)).unwrap();
        toast.success('Leave limit added successfully!');
      }
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.message || 'Error processing request');
    }
  };

  const handleEdit = (item) => {
    setIsEdit(true);
    setCurrentItem(item);
    setFormData({
      leaveType: item.leaveType,
      maxDays: item.maxDays,
      isPaid: item.isPaid,
      description: item.description || '',
      isActive: item.isActive,
      autoAssign: item.autoAssign
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this leave limit?')) {
      try {
        await dispatch(deleteLeaveLimit(id)).unwrap();
        toast.success('Leave limit deleted successfully!');
      } catch (error) {
        toast.error('Error deleting leave limit');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await dispatch(toggleLeaveLimitStatus(id)).unwrap();
      toast.success('Status toggled successfully!');
    } catch (error) {
      toast.error('Error toggling status');
    }
  };

  const handleToggleAutoAssign = async (id) => {
    try {
      await dispatch(toggleLeaveLimitAutoAssign(id)).unwrap();
      toast.success('Auto-assign toggled successfully!');
    } catch (error) {
      toast.error('Error toggling auto-assign');
    }
  };

  const resetForm = () => {
    setFormData({
      leaveType: '',
      maxDays: 0,
      isPaid: true,
      description: '',
      isActive: true,
      autoAssign: true
    });
    setIsEdit(false);
    setCurrentItem(null);
  };

  const getStatusColor = (isActive) => {
    return isActive 
      ? 'text-green-600 bg-green-100' 
      : 'text-red-600 bg-red-100';
  };

  const getPaidStatusColor = (isPaid) => {
    return isPaid 
      ? 'text-blue-600 bg-blue-100' 
      : 'text-orange-600 bg-orange-100';
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Leave Type Management</h1>
        <div className="flex space-x-2">
          <button
            onClick={handleInitializeDefaultTypes}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
          >
            <Settings size={20} />
            Initialize Default Types
          </button>
          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="bg-[#241763] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#1a0f47]"
          >
            <Plus size={20} />
            Add Leave Type
          </button>
        </div>
      </div>

      {/* Default Leave Types Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-800">Default Leave Types</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-600" />
              <span className="font-medium">Casual Leave</span>
            </div>
            <p className="text-sm text-gray-600">14 days paid</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Medical Leave</span>
            </div>
            <p className="text-sm text-gray-600">14 days paid</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-600" />
              <span className="font-medium">Annual Leave</span>
            </div>
            <p className="text-sm text-gray-600">14 days paid</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-orange-600" />
              <span className="font-medium">Without Pay Leave</span>
            </div>
            <p className="text-sm text-gray-600">30 days unpaid</p>
          </div>
        </div>
      </div>

      {/* Leave Limits Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Leave Limits</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leave Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max Days
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auto-Assign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leaveLimits.map((limit) => (
                <tr key={limit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {limit.leaveType}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{limit.maxDays} days</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaidStatusColor(limit.isPaid)}`}>
                      {limit.isPaid ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(limit.isActive)}`}>
                      {limit.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${limit.autoAssign ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'}`}>
                      {limit.autoAssign ? '✅ Enabled' : '❌ Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {limit.description || 'No description'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(limit)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(limit.id)}
                        className="text-green-600 hover:text-green-900"
                        title={limit.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {limit.isActive ? <XCircle size={16} /> : <CheckCircle size={16} />}
                      </button>
                      <button
                        onClick={() => handleToggleAutoAssign(limit.id)}
                        className={`${limit.autoAssign ? 'text-blue-600 hover:text-blue-900' : 'text-gray-600 hover:text-gray-900'}`}
                        title={limit.autoAssign ? 'Disable Auto-Assign' : 'Enable Auto-Assign'}
                      >
                        {limit.autoAssign ? <CheckCircle size={16} /> : <XCircle size={16} />}
                      </button>
                      <button
                        onClick={() => handleDelete(limit.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isEdit ? 'Edit Leave Limit' : 'Add Leave Limit'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Leave Type
                  </label>
                  <input
                    type="text"
                    value={formData.leaveType}
                    onChange={(e) => setFormData({...formData, leaveType: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Days
                  </label>
                  <input
                    type="number"
                    value={formData.maxDays}
                    onChange={(e) => setFormData({...formData, maxDays: parseInt(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isPaid}
                      onChange={(e) => setFormData({...formData, isPaid: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Paid Leave</span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    rows="3"
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.autoAssign}
                      onChange={(e) => setFormData({...formData, autoAssign: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Auto-Assign to New Employees</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    When enabled, this leave type will be automatically assigned to new employees
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-[#241763] rounded-md hover:bg-[#1a0f47]"
                  >
                    {isEdit ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypeManagement; 